import React, { useMemo, useState } from "react";

import { useLocations, useProfile, useQueryString } from "app/context";
import { t, Trans } from "app/utils";

import FilterAccordian from "components/common/FilterAccordian";
import EntityFilterState from "./EntityFilterState";
import ListItem from "./ListItem";

export const queryKey = "l";

const LocationFilterItem = ({ active, location, onClick }) => {
  const { profile } = useProfile();

  let label = location?.name;
  if (profile.location?.id === location?.id) {
    label = {
      label,
      legend: "(My location)"
    };
  }

  return <ListItem key={location?.id} active={active} label={label} onClick={onClick} />;
};

const LocationFilter = () => {
  const {
    allLocationsFetched,
    locations,
    onAutocompleteLocations,
    onNextLocationPageRequested,
    onLocationsRequested
  } = useLocations();

  const [search, setSearch] = useState("");
  const onSearch = query => {
    onAutocompleteLocations(query);
    setSearch(query);
  };

  const { queryString, onClearQueryStringKey, onToggleQueryStringValue } = useQueryString();
  const locationIds = queryString[queryKey];

  // Ensure all of the location ids that we actually need are in the cache
  if (locationIds) {
    const missing = locationIds.filter(locationId => (
      !Object.prototype.hasOwnProperty.call(locations, locationId)
    ));

    if (missing.length > 0) {
      onLocationsRequested(missing);
    }
  }

  const onAllClick = () => onClearQueryStringKey(queryKey);
  const onLocationClick = locationId => onToggleQueryStringValue(queryKey, locationId);

  const activeLocations = useMemo(
    () => {
      const searchTerm = search.toLowerCase();
      const active = [];

      Object.keys(locations).forEach(locationId => {
        const location = locations[locationId];

        if (location?.name?.toLowerCase()?.includes(searchTerm)) {
          active.push(location);
        }
      });

      return active.sort((a, b) => a.name.localeCompare(b.name));
    },
    [search, locations]
  );

  const onScroll = allLocationsFetched ? null : onNextLocationPageRequested;

  return (
    <FilterAccordian title={t("Locations")} onScroll={onScroll} onSearch={onSearch}>
      <ul>
        <ListItem label={t("All Locations")} onClick={onAllClick} active={!locationIds} />
        {activeLocations.map(location => (
          <LocationFilterItem
            key={location.id}
            location={location}
            onClick={() => onLocationClick(location.id)}
            active={locationIds && locationIds.includes(location.id)}
          />
        ))}
      </ul>
    </FilterAccordian>
  );
};

export const LocationFilterState = () => {
  const { profile } = useProfile();
  const { locations } = useLocations();

  return (
    <EntityFilterState entities={locations} queryKey={queryKey}>
      {locationId => profile.location?.id === locationId && <Trans>(My Location)</Trans>}
    </EntityFilterState>
  );
};

export default LocationFilter;
